import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SideBar from '../../components/Topbar';
import { HTMLCourseNavProvider } from "../../components/SidebarCourseContext";
import './assets/styles/HTMLLesson1.css';
import { CopyBlock, dracula } from "react-code-blocks";

// Import sophisticated icons - using available ones
import { 
  FaCode,
  FaCheck,
  FaArrowRight,
  FaPlay,
  FaBookOpen,
  FaTextHeight,
  FaQuoteLeft, // Replaced FaPoem with FaQuoteLeft
  FaAlignLeft
} from 'react-icons/fa';

const HTMLLesson4 = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [viewedSections, setViewedSections] = useState(new Set([0]));
  const sectionRefs = useRef([]);
  const observerRef = useRef(null);

  const [language] = useState("html");
  const [lineNumbers] = useState(true);

  // Video explanation URL
  const videoUrl = "https://app.codeflarelimited.com/watch-video/html-course";

  const code1 = `<p>This is a <br> paragraph <br> with <br> line breaks.</p>`;

  const code2 = `<p>
Go, soul, the body's guest,
Upon a thankless errand;
Fear not to touch the best;
The truth shall be thy warrant.
Go, since I needs must die,
And give the world the lie.
</p>`;

  const code3 = `<pre>
Go, soul, the body's guest,
Upon a thankless errand;
Fear not to touch the best;
The truth shall be thy warrant.
Go, since I needs must die,
And give the world the lie.
</pre>`;

  const sections = [
    {
      title: "HTML Line Breaks",
      content: "The HTML <br> element defines a line break and is used to create new lines without starting new paragraphs",
      keyPoints: [
        "Use <br> for line breaks within the same paragraph",
        "The <br> tag is an empty tag (no closing tag needed)",
        "It's perfect for addresses, poetry, and formatted text"
      ],
      icon: <FaTextHeight className="section-icon" />,
      color: "#2563eb",
      code: code1
    },
    {
      title: "The Poem Problem", 
      content: "Understanding how HTML handles whitespace and why poems often display incorrectly",
      keyPoints: [
        "HTML collapses multiple spaces and line breaks by default",
        "Poems and formatted text lose their structure in regular paragraphs",
        "This demonstrates why we need special formatting elements"
      ],
      icon: <FaQuoteLeft className="section-icon" />, // Changed from FaPoem to FaQuoteLeft
      color: "#1d4ed8",
      code: code2
    },
    {
      title: "Preformatted Text",
      content: "The HTML <pre> element preserves both spaces and line breaks exactly as written",
      keyPoints: [
        "<pre> text displays in a fixed-width font (usually Courier)",
        "It preserves all whitespace and line breaks",
        "Perfect for code blocks, poetry, and ASCII art"
      ],
      icon: <FaAlignLeft className="section-icon" />,
      color: "#1e40af",
      code: code3
    }
  ];

  // Rest of the component remains exactly the same...
  // Intersection Observer for scroll detection
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '-10% 0px -20% 0px',
      threshold: 0.6
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const index = sectionRefs.current.indexOf(entry.target);
        
        if (entry.isIntersecting) {
          setActiveSection(index);
          setViewedSections(prev => new Set([...prev, index]));
        }
      });
    }, options);

    sectionRefs.current.forEach((section) => {
      if (section) {
        observerRef.current.observe(section);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const addSectionRef = (element, index) => {
    sectionRefs.current[index] = element;
  };

  const getCompletionPercentage = () => {
    const totalSections = sections.length + 2;
    const viewedCount = viewedSections.size;
    return Math.round((viewedCount / totalSections) * 100);
  };

  return (
    <HTMLCourseNavProvider>
      <div className="html-lesson-container">
        <SideBar />
        
        <div className="html-lesson-main">
          {/* Header Section */}
          <div className="html-lesson-header">
            <div className="html-header-content">
              <div className="html-lesson-badge">
                <FaBookOpen className="html-badge-icon" />
                Lesson 4 • {getCompletionPercentage()}% Viewed
              </div>
              <h1 className="html-lesson-title">HTML Line Breaks & Preformatted Text</h1>
              <p className="html-lesson-subtitle">
                Master line breaks, preserve formatting, and display text exactly as intended
              </p>
              
              {/* Progress Bar */}
              <div className="html-completion-progress">
                <div 
                  className="html-completion-fill"
                  style={{ width: `${getCompletionPercentage()}%` }}
                ></div>
                <div className="html-progress-labels">
                  <span>Getting Started</span>
                  <span>Complete</span>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="html-progress-indicator">
            {sections.map((section, index) => (
              <div 
                key={index}
                className={`html-progress-step ${viewedSections.has(index) ? 'html-viewed' : ''} ${index === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-step-number">
                  {viewedSections.has(index) ? '✓' : index + 1}
                </div>
                <div className="html-step-label">{section.title}</div>
                {index < sections.length - 1 && <div className="html-step-connector"></div>}
              </div>
            ))}
          </div>

          {/* Content Sections */}
          <div className="html-content-sections">
            {sections.map((section, index) => (
              <div 
                key={index}
                ref={(el) => addSectionRef(el, index)}
                className={`html-content-card ${viewedSections.has(index) ? 'html-viewed' : ''} ${index === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-card-badge">
                  {viewedSections.has(index) ? '✓ Viewed' : `Part ${index + 1}`}
                </div>
                
                <div className="html-card-header">
                  <div className="html-card-icon" style={{ backgroundColor: section.color }}>
                    {section.icon}
                  </div>
                  <div className="html-card-text">
                    <h2 className="html-card-title">{section.title}</h2>
                    <p className="html-card-content" dangerouslySetInnerHTML={{ __html: section.content }} />
                    
                    <div className="html-key-points">
                      <h4>Key Points:</h4>
                      <ul>
                        {section.keyPoints.map((point, idx) => (
                          <li key={idx} dangerouslySetInnerHTML={{ __html: point }} />
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Code Example for each section */}
                <div className="html-section-code">
                  <div className="html-code-badge">
                    <FaCode className="html-badge-icon" />
                    Example
                  </div>
                  <div className="html-code-block-small">
                    <CopyBlock
                      language={language}
                      text={section.code}
                      showLineNumbers={lineNumbers}
                      theme={dracula}
                      wrapLines={true}
                      codeBlock
                    />
                  </div>
                  
                  {/* Visual Output Preview */}
                  <div className="html-output-preview">
                    <h5>Browser Output:</h5>
                    <div className="html-output-content">
                      {index === 0 && (
                        <div style={{ padding: '1rem', background: 'white', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                          <p style={{ margin: 0 }}>This is a<br />paragraph<br />with<br />line breaks.</p>
                        </div>
                      )}
                      {index === 1 && (
                        <div style={{ padding: '1rem', background: 'white', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                          <p style={{ margin: 0 }}>Go, soul, the body's guest, Upon a thankless errand; Fear not to touch the best; The truth shall be thy warrant. Go, since I needs must die, And give the world the lie.</p>
                        </div>
                      )}
                      {index === 2 && (
                        <div style={{ padding: '1rem', background: 'white', borderRadius: '4px', border: '1px solid #e2e8f0', fontFamily: 'monospace', whiteSpace: 'pre' }}>
                          Go, soul, the body's guest,<br />
                          Upon a thankless errand;<br />
                          Fear not to touch the best;<br />
                          The truth shall be thy warrant.<br />
                          Go, since I needs must die,<br />
                          And give the world the lie.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="html-card-actions">
                  <a 
                    href={videoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="html-action-btn"
                  >
                    <FaPlay className="html-btn-icon" />
                    Watch Video Explanation
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Important Notes Section */}
          <div 
            className="html-notes-section"
            ref={(el) => addSectionRef(el, sections.length)}
          >
            <div className="html-notes-header">
              <div className="html-code-badge">
                <FaCode className="html-badge-icon" />
                Important Notes
              </div>
              <h3 className="html-notes-title">
                Key Differences & Best Practices
              </h3>
            </div>
            
            <div className="html-notes-grid">
              <div className="html-note-card">
                <div className="html-note-icon" style={{ backgroundColor: '#10b981' }}>
                  <FaTextHeight />
                </div>
                <h4>When to Use &lt;br&gt;</h4>
                <ul>
                  <li>Addresses and contact information</li>
                  <li>Poetry and song lyrics</li>
                  <li>Forced line breaks within paragraphs</li>
                  <li>Anywhere you need a new line without extra spacing</li>
                </ul>
              </div>
              
              <div className="html-note-card">
                <div className="html-note-icon" style={{ backgroundColor: '#3b82f6' }}>
                  <FaAlignLeft />
                </div>
                <h4>When to Use &lt;pre&gt;</h4>
                <ul>
                  <li>Code blocks and examples</li>
                  <li>Formatted poetry with indentation</li>
                  <li>ASCII art and diagrams</li>
                  <li>Any text where spacing matters</li>
                </ul>
              </div>
              
              <div className="html-note-card">
                <div className="html-note-icon" style={{ backgroundColor: '#f59e0b' }}>
                  <FaCode />
                </div>
                <h4>Technical Details</h4>
                <ul>
                  <li>&lt;br&gt; is an empty element (no closing tag)</li>
                  <li>&lt;pre&gt; preserves ALL whitespace</li>
                  <li>Both elements are inline by default</li>
                  <li>Use CSS for more complex formatting</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Comprehensive Code Example */}
          <div 
            className="html-code-example"
            ref={(el) => addSectionRef(el, sections.length + 1)}
          >
            <div className="html-code-header">
              <div className="html-code-badge">
                <FaCode className="html-badge-icon" />
                Complete Example
              </div>
              <h3 className="html-code-title">
                Real-World Usage Examples
              </h3>
            </div>
            <div className="html-code-block">
              <CopyBlock
                language={language}
                text={`<!DOCTYPE html>
<html>
<head>
    <title>Line Breaks & Preformatted Text</title>
</head>
<body>
    <!-- Using <br> for addresses -->
    <address>
        John Doe<br>
        123 Main Street<br>
        New York, NY 10001<br>
        USA
    </address>
    
    <!-- Using <br> in poetry -->
    <p>
        Two roads diverged in a yellow wood,<br>
        And sorry I could not travel both<br>
        And be one traveler, long I stood<br>
        And looked down one as far as I could
    </p>
    
    <!-- Using <pre> for code -->
    <pre>
function helloWorld() {
    console.log("Hello, World!");
    return true;
}
    </pre>
    
    <!-- Using <pre> for formatted text -->
    <pre>
    ╔═══════════════╗
    ║   WARNING!    ║
    ╠═══════════════╣
    ║ System Update ║
    ║   Required    ║
    ╚═══════════════╝
    </pre>
    
    <!-- Mixing both approaches -->
    <p>
        Regular paragraph text.<br>
        <pre>Preformatted section within paragraph</pre>
        More regular text.
    </p>
</body>
</html>`}
                showLineNumbers={lineNumbers}
                theme={dracula}
                wrapLines={true}
                codeBlock
              />
            </div>
            <div className="html-code-actions">
              <a 
                href={videoUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="html-code-btn html-primary"
              >
                Watch Complete Implementation Guide
              </a>
            </div>
          </div>

          {/* Action Section */}
          <div 
            className="html-action-section"
            ref={(el) => addSectionRef(el, sections.length + 2)}
          >
            <div className="html-action-header">
              <h3>Mastered line breaks and formatting?</h3>
              <div className="html-completion-badge">
                {getCompletionPercentage()}% of content viewed
              </div>
            </div>
            
            <div className="html-action-content">
              <p>You've learned how to control line breaks and preserve text formatting. Practice using &lt;br&gt; and &lt;pre&gt; in your projects!</p>
              
              <div className="html-action-buttons">
                <button 
                  className={`html-complete-button ${isCompleted ? 'html-completed' : ''}`}
                  onClick={() => setIsCompleted(true)}
                >
                  {isCompleted ? (
                    <>
                      <FaCheck className="html-button-icon" />
                      Lesson Completed
                    </>
                  ) : (
                    'Mark as Complete'
                  )}
                </button>
                
                {isCompleted && (
                  <Link to="/html-headings" className="html-next-lesson-link">
                    Continue to Next Lesson
                    <FaArrowRight className="html-link-icon" />
                  </Link>
                )}
              </div>
              
              <div className="html-quick-nav">
                <span>Need more examples? </span>
                <a 
                  href={videoUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="html-nav-link"
                >
                  Watch Complete HTML Course
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </HTMLCourseNavProvider>
  );
}

export default HTMLLesson4;